import { roomIdSchema } from '@/transport/protocol';

/**
 * Ідентифікатор кімнати у форматі мобільних клієнтів:
 * `timestamp-xxxxxx-xxxxxx-xxxxxx`. Формат спільний — за ним діляться
 * посиланнями між платформами.
 */
export function generateRoomId(): string {
  const bytes = new Uint8Array(9);
  crypto.getRandomValues(bytes);
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${String(Date.now())}-${hex.slice(0, 6)}-${hex.slice(6, 12)}-${hex.slice(12, 18)}`;
}

export function isValidRoomId(value: string): boolean {
  return roomIdSchema.safeParse(value).success;
}

/**
 * Дістає ідентифікатор кімнати з адреси сторінки.
 *
 * Основний варіант — `?room=<id>`: саме такі посилання генерують мобільні
 * клієнти зі стору, і саме такі вже розіслані користувачами. Запасний
 * `#room=<id>` лишається підтриманим, бо для нього у Flutter-клієнті існував
 * окремий шлях розбору під Safari.
 *
 * Порожній результат — нормальний випадок (просто відкрили головну), а не помилка.
 */
export function extractRoomIdFromUrl(href: string): string | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }

  const fromQuery = url.searchParams.get('room');
  if (fromQuery !== null && fromQuery.length > 0) return fromQuery;

  const hash = url.hash.startsWith('#') ? url.hash.slice(1) : url.hash;
  if (hash.length === 0) return null;

  // У фрагменті може бути як `room=<id>`, так і кілька параметрів.
  const fromHash = new URLSearchParams(hash).get('room');
  return fromHash !== null && fromHash.length > 0 ? fromHash : null;
}

const LINK_SCHEMES = new Set(['http:', 'https:', 'one2one:']);

function parseLink(input: string): URL | null {
  try {
    const url = new URL(input);
    return LINK_SCHEMES.has(url.protocol.toLowerCase()) ? url : null;
  } catch {
    return null;
  }
}

/**
 * Що вставили в поле приєднання: посилання з `?room=` або сам ідентифікатор.
 *
 * Людина, якій надіслали посилання, а сторінку вона відкрила сама, вставляє
 * в поле саме посилання. Хост навмисно не перевіряється — та сама таблиця
 * кейсів у `lib/domain/video_call/room_link_parser.dart`.
 */
export function parseRoomInput(raw: string): string | null {
  const input = raw.trim();
  if (input.length === 0) return null;

  const url = parseLink(input);
  if (url !== null) {
    const room = url.searchParams.get('room')?.trim() ?? '';
    return room.length > 0 && isValidRoomId(room) ? room : null;
  }
  return isValidRoomId(input) ? input : null;
}

/** Вставлене саме посилання (для `room_joined.via`). */
export function looksLikeRoomLink(raw: string): boolean {
  return parseLink(raw.trim()) !== null;
}
